<template>
  <div>
    <section
      v-if="timestampEnabled"
      class="info-tiles"
      style="margin-bottom: 1rem"
    >
      <div class="tile is-ancestor has-text-centered">
        <div class="tile is-parent">
          <article class="tile is-child box">
            <p class="title">
              {{ timeOfDay.toLocaleTimeString() }}
            </p>
            <p class="subtitle">
              Timestamp
            </p>
          </article>
        </div>
      </div>
    </section>
    <section>
      <TableHeader
        :title="dataView ? 'Data' : 'Status'"
        :background-primary="true"
      >
        <template
          v-if="dashboardState.status.length != 0"
          #default
        >
          <b-tooltip
            position="is-left"
            type="is-dark"
            label="Number of active devices"
          >
            <b-tag
              size="is-medium"
              type="is-dark"
              style="margin-top: 0.15rem"
            >
              {{ deviceStatusCount[0] + deviceStatusCount[1] }} /
              {{ deviceStatusCount[3] }}
            </b-tag>
          </b-tooltip>
        </template>
        <template #custom>
          <b-button
            icon-left="magnify"
            style="margin-right: 0.75rem"
            type="is-dark"
            :focused="isSearchActive"
            rounded
            @click="isSearchActive = !isSearchActive"
          >
            <span v-if="isSearchActive">Hide search</span>
            <span v-else>Search</span>
          </b-button>
          <download-csv
            :data="
              dashboardState.status.length == 0 ? [] : dashboardState.status
            "
            name="export.csv"
            class="button is-dark is-rounded"
          >
            <b-icon
              pack="fas"
              icon="file-csv"
            />
          </download-csv>
        </template>
      </TableHeader>
      <b-table
        ref="status-table"
        class="table-status"
        :data="dashboardState.status.length == 0 ? [] : dashboardState.status"
        :bordered="false"
        :striped="true"
        :narrowed="true"
        :hoverable="false"
        :loading="false"
        :focusable="false"
        :mobile-cards="false"
        sort-icon="arrow-up"
        sort-icon-size="is-small"
        default-sort-direction="desc"
        default-sort="date_updated"
        :per-page="40"
        :paginated="true"
      >
        <template slot-scope="props">
          <b-table-column
            field="telemetry-btn"
            label
            style="white-space: nowrap"
          >
            <b-dropdown aria-role="list">
              <a
                slot="trigger"
                role="button"
                @click="
                  activeTelemetryUUID = props.row.telemetry;
                  activeUUID = props.row.id;
                  activeDeviceEUI = props.row.payload_id;
                  activeDeviceName = props.row.upstream_dev_id;
                "
              >
                <b-icon
                  pack="fas"
                  icon="chevron-circle-down"
                />
              </a>

              <b-dropdown-item
                aria-role="listitem"
                @click="
                  isTelemetryInspectorActive = !isTelemetryInspectorActive
                "
              >
                <div class="media">
                  <b-icon
                    class="media-left"
                    pack="fas"
                    icon="glasses"
                  />
                  <div class="media-content">
                    <h3>Telemetry Inspector</h3>
                    <small>Open the Telemetry Inspector tool</small>
                  </div>
                </div>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="!telemetrySearchStandalone"
                aria-role="listitem"
                @click="isTelemetrySearchActive = !isTelemetrySearchActive"
              >
                <div class="media">
                  <b-icon
                    class="media-left"
                    pack="fas"
                    icon="table"
                  />
                  <div class="media-content">
                    <h3>Telemetry Search</h3>
                    <small>Open the Telemetry Search tool</small>
                  </div>
                </div>
              </b-dropdown-item>

              <b-dropdown-item
                aria-role="listitem"
                @click="promptChangeDescription(activeUUID, props.row.description)"
              >
                <div class="media">
                  <b-icon
                    class="media-left"
                    pack="fas"
                    icon="edit"
                  />
                  <div class="media-content">
                    <h3>Change device description</h3>
                    <small>Change the description.<br>The description will also be
                      public, if the device is public.</small>
                  </div>
                </div>
              </b-dropdown-item>

              <b-dropdown-item
                :disabled="props.row.latitude == 0 && props.row.longitude == 0"
                aria-role="listitem"
                @click="showOnMap(activeUUID)"
              >
                <div class="media">
                  <b-icon
                    class="media-left"
                    pack="fas"
                    icon="map-marked"
                  />
                  <div class="media-content">
                    <h3>Show on map</h3>
                    <small>Open the map and show the device location</small>
                  </div>
                </div>
              </b-dropdown-item>
            </b-dropdown>
            <a
              v-if="telemetrySearchStandalone"
              style="padding-left: 0.2rem"
              role="button"
              @click="
                activeTelemetryUUID = props.row.telemetry;
                activeUUID = props.row.id;
                activeDeviceEUI = props.row.payload_id;
                activeDeviceName = props.row.upstream_dev_id;
                isTelemetrySearchActive = !isTelemetrySearchActive;
              "
            >
              <b-icon
                pack="fas"
                icon="table"
              />
            </a>
          </b-table-column>
          <b-table-column
            field="description"
            label="Description"
            :searchable="isSearchActive"
          >
            <span v-if="props.row.hasOwnProperty('description')"><span v-if="props.row.description.length < 11">
                                                                   {{ props.row.description }}
                                                                 </span>
              <b-tooltip
                :active="props.row.hasOwnProperty('description')"
                :label="props.row.description"
                position="is-right"
                size="is-medium"
                multilined
              >
                <span v-if="!(props.row.description.length < 11)">
                  {{ props.row.description.substring(0, 11) + "..." }}
                </span>
              </b-tooltip></span>
          </b-table-column>
          <b-table-column
            field="payload_id"
            label="EUI"
            :searchable="isSearchActive"
          >
            <a
              v-clipboard:copy="props.row.payload_id"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
            >{{ props.row.payload_id }}</a>
          </b-table-column>

          <b-table-column
            field="upstream_dev_id"
            label="Device ID"
            sortable
            :searchable="isSearchActive"
          >
            {{ props.row.upstream_dev_id }}
          </b-table-column>

          <b-table-column
            field="organisation"
            label="Organisation"
            sortable
          >
            <p
              v-if="
                findByField(
                  dashboardState.organisations,
                  'id',
                  props.row.organisation
                ) != null
              "
            >
              {{
                !dashboardState.organisations.length == 0
                  ? findByField(
                    dashboardState.organisations,
                    "id",
                    props.row.organisation
                  ).name
                  : props.row.organisation
              }}
            </p>
            <span
              v-else
              class="tag is-danger"
            >
              <b>ORGANISATION UNAVAILABLE</b>
            </span>
          </b-table-column>

          <b-table-column
            field="device_type"
            label="Device Type"
          >
            <p
              v-if="
                findByField(dashboardState.devices, 'id', props.row.device) !=
                  null
              "
            >
              {{
                !dashboardState.devices.length == 0
                  ? findByField(dashboardState.devices, "id", props.row.device)
                    .name
                  : props.row.device
              }}
            </p>
            <span v-else>
              <p
                v-if="
                  findByField(
                    dashboardState.devicesshared,
                    'id',
                    props.row.device
                  ) != null
                "
              >
                {{
                  !dashboardState.devicesshared.length == 0
                    ? findByField(
                      dashboardState.devicesshared,
                      "id",
                      props.row.device
                    ).name
                    : props.row.device
                }}
              </p>
              <b-tooltip
                v-else
                label="Owner of this device has stopped sharing access to it. Please contact the site administator for more information."
                type="is-dark"
                position="is-bottom"
                animated
                multilined
              >
                <span class="tag is-danger">
                  <b>DEVICES PAUSED</b>
                </span>
              </b-tooltip>
            </span>
          </b-table-column>

          <b-table-column
            v-if="!dataView"
            field="metadata.upstream_metadata.data_rate"
            label="DR"
            sortable
            numeric
          >
            {{ props.row.metadata.upstream_metadata.data_rate }}
          </b-table-column>

          <b-table-column
            v-if="!dataView"
            label="RSSI"
            numeric
          >
            {{ getBestRSSI(props.row.metadata.upstream_metadata.gateways) }}
          </b-table-column>

          <b-table-column
            v-if="!dataView"
            label="SNR"
            numeric
          >
            {{ getBestSNR(props.row.metadata.upstream_metadata.gateways) }}
          </b-table-column>

          <b-table-column
            v-if="!dataView"
            field="metadata.upstream_counter"
            label="Counter"
            sortable
            numeric
          >
            {{ props.row.metadata.upstream_counter }}
          </b-table-column>

          <b-table-column
            v-if="dataView"
            label="Payload"
            style="max-width: 40rem"
          >
            {{ getFormattedPayload(props.row.metadata.payload) }}
          </b-table-column>

          <b-table-column
            v-if="!dataView"
            field="gtw_seen"
            label="Gateways"
            sortable
            numeric
          >
            <a
              @click="
                activeTelemetryUUID = props.row.telemetry;
                isGatewayInspectorActive = !isGatewayInspectorActive;
              "
            >{{ props.row.gtw_seen }}</a>
          </b-table-column>

          <b-table-column
            field="date_updated"
            label="Status"
            sortable
            centered
          >
            <b-tooltip
              :label="telemetryStateTooltip(props.row.date_updated, timeOfDay)"
              type="is-dark"
              position="is-bottom"
              animated
            >
              <status-indicator
                :status="
                  telemetryStateBullet(props.row.date_updated, timeOfDay)
                "
                pulse
              />
            </b-tooltip>
          </b-table-column>
        </template>
        <template slot="empty">
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p>
                <b-icon
                  icon="access-point"
                  pack="mdi"
                  size="is-large"
                />
              </p>
              <p>Devices are unavailable.</p>
            </div>
          </section>
        </template>
      </b-table>
    </section>
    <div>
      <b-modal
        :active.sync="isTelemetryInspectorActive"
        :can-cancel="true"
        has-modal-card
      >
        <TelemetryInspectorComponent :uuid="activeTelemetryUUID" />
      </b-modal>
      <b-modal
        :active.sync="isGatewayInspectorActive"
        :can-cancel="true"
        has-modal-card
      >
        <GatewayInspectorComponent :uuid="activeTelemetryUUID" />
      </b-modal>
      <b-modal
        :active.sync="isTelemetrySearchActive"
        :can-cancel="true"
        has-modal-card
      >
        <TelemetrySearch
          class="is-telemetry-search-modal"
          :allow-custom-eui="false"
          :passed-eui="activeDeviceEUI"
          :passed-device-name="activeDeviceName"
        />
      </b-modal>
    </div>
  </div>
</template>

<script>
import TelemetryInspectorComponent from "@/components/TelemetryInspector";
import GatewayInspectorComponent from "@/components/GatewayInspector";
import TelemetrySearch from "@/components/TelemetrySearch";
import TableHeader from "@/components/elements/TableHeader.vue";
import TelemetryApi from "@/services/Telemetry";
import Errors from "@/services/Error";
import Tools from "@/services/Tools";
import { mapState, mapActions, mapGetters } from "vuex";

export default {
  components: {
    TelemetryInspectorComponent,
    GatewayInspectorComponent,
    TelemetrySearch,
    TableHeader,
  },
  props: {
    timestampEnabled: Boolean,
    dataView: Boolean,
    telemetrySearchStandalone: Boolean,
  },
  data() {
    return {
      activeTelemetryUUID: "00000000-0000-0000-0000-000000000000",
      activeUUID: "00000000-0000-0000-0000-000000000000",
      activeDeviceEUI: "",
      activeDeviceName: "",
      isTelemetryInspectorActive: false,
      isGatewayInspectorActive: false,
      isTelemetrySearchActive: false,
      isSearchActive: false,
    };
  },
  computed: {
    deviceStatusCount: function () {
      var activeDevices = 0;
      var inactiveDevices = 0;
      var offlineDevices = 0;
      var allDevices = this.dashboardState.status.length;
      this.dashboardState.status.forEach((device) => {
        let timeDiff = Tools.timeDiff(device.date_updated, this.timeOfDay);
        if (timeDiff > 0 && timeDiff < 3600) {
          activeDevices++;
        } else if (timeDiff < 86400) {
          inactiveDevices++;
        } else {
          offlineDevices++;
        }
      });
      return [activeDevices, inactiveDevices, offlineDevices, allDevices];
    },
    isLoginActive: {
      get() {
        return this.$store.state.loginActive;
      },
      set(value) {
        this.$store.commit("updateLoginActive", value);
      },
    },
    timeOfDay: {
      get() {
        return this.$store.state.currentTime;
      },
      set(value) {
        this.$store.commit("updateCurrentTime", value);
      },
    },
    ...mapState({
      dashboardState: "dashboardStates",
    }),
  },
  methods: {
    ...mapGetters(["UserLoggedIn"]),
    ...mapActions({
      throwGlobalNotification: "throwGlobalNotification",
      getStatus: "getStatus",
    }),
    findByField: Tools.findByField,
    telemetryStateBullet(updateDate, currentDate) {
      let timeDiff = Tools.timeDiff(updateDate, currentDate);

      if (timeDiff > 0 && timeDiff < 3600) {
        return "positive";
      } else if (timeDiff < 86400) {
        return "intermediary";
      }

      return "negative";
    },
    telemetryStateTooltip(updateDate, currentDate) {
      let timeDiff = Tools.timeDiff(updateDate, currentDate);

      if (timeDiff > 0 && timeDiff < 60) {
        let roundedTimeDiff = Math.round(timeDiff);
        if (roundedTimeDiff == 1) {
          return roundedTimeDiff + " second ago";
        } else {
          return roundedTimeDiff + " seconds ago";
        }
      } else if (timeDiff < 900) {
        let roundedTimeDiff = Math.round(timeDiff / 60);
        if (roundedTimeDiff == 1) {
          return roundedTimeDiff + " minute ago";
        } else {
          return roundedTimeDiff + " minutes ago";
        }
      } else {
        return new Date(updateDate).toLocaleString();
      }
    },
    getBestSNR(gateways) {
      if (Array.isArray(gateways)) {
        var best_snr = -20;
        gateways.forEach((gateway) => {
          if (gateway.snr > best_snr) {
            best_snr = gateway.snr;
          }
        });
        return best_snr;
      }
    },
    getBestRSSI(gateways) {
      if (Array.isArray(gateways)) {
        var best_rssi = -120;
        gateways.forEach((gateway) => {
          if (gateway.rssi > best_rssi) {
            best_rssi = gateway.rssi;
          }
        });
        return best_rssi;
      }
    },
    getFormattedPayload(payload) {
      if (payload.length == 0) {
        return "";
      }

      let payload_json = JSON.parse(payload);
      let formatted_string = "";

      for (var key in payload_json) {
        formatted_string += key + ": " + payload_json[key] + ", ";
      }

      return formatted_string.substring(0, formatted_string.length - 2);
    },
    promptChangeDescription(uuid, value) {
      this.$buefy.dialog.prompt({
        message: `Change device description`,
        inputAttrs: {
          placeholder: "Device description",
          value: value,
          required: false,
          maxlength: 150,
        },
        onConfirm: (value) => {
          TelemetryApi.telemetryLatest(uuid, value)
            .then((result) => {
              this.getStatus();
              this.throwGlobalNotification({
                message: result.status_message,
                queue: true,
                isError: false,
                debug: true,
              });
            })
            .catch((err) => {
              if (err.name == Errors.API.BadRequest.name) {
                this.throwGlobalNotification({
                  message:
                    "You are using invalid characters or the text length is insufficient",
                  queue: false,
                  isError: true,
                });
              } else if (err.name == Errors.API.UnexpectedErr.name) {
                this.throwGlobalNotification({
                  message: "An unexpected error has occurred",
                  queue: false,
                  isError: true,
                });
              } else if (err.name == Errors.API.ServerInternalErr.name) {
                this.throwGlobalNotification({
                  message: "Internal server error has occurred",
                  queue: false,
                  isError: true,
                });
              }
            });
        },
      });
    },
    showOnMap(uuid) {
      this.$router.push({
        name: "devicemap",
        query: { uuid: uuid },
      });
    },
    // eslint-disable-next-line
    onCopy(e) {
      this.throwGlobalNotification({
        message: "Copied to clipboard",
        queue: true,
        isError: false,
      });
    },
    // eslint-disable-next-line
    onError(e) {
      this.throwGlobalNotification({
        message: "Failed to copy to clipboard",
        queue: false,
        isError: true,
      });
    },
  },
};
</script>

<style>
.table-status {
  margin: 0;
  padding: 0;
  padding-bottom: 3.2rem;
}
.is-telemetry-search-modal {
  min-height: 13rem;
  min-width: 22rem;
  overflow-y: auto;
}
</style>

